/**
 * Client detail (spec §8.7), built on the shared detail-page skeleton
 * (components/common/detail-page.tsx) so it presents the same shape as every other
 * entity: breadcrumb → identity → metrics → tabs → workspace.
 */

import * as React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Car,
  Download,
  Mail,
  MapPin,
  MessageSquarePlus,
  Pencil,
  Phone,
  Plus,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/misc';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EnumBadge, useEnumOptions } from '@/components/common/enum-badge';
import { DocumentPanel } from '@/components/common/document-panel';
import { EmptyState, Field, FieldGrid } from '@/components/common/page-header';
import {
  DetailHeader,
  DetailMetrics,
  DetailPage,
  DetailPageSkeleton,
  DetailTabsBar,
  Metric,
  Panel,
  Section,
  SectionStack,
  Workspace,
} from '@/components/common/detail-page';
import { EntityAvatar } from '@/components/common/entity-avatar';
import { PolicyRef } from '@/components/common/policy-ref';
import { download } from '@/lib/api/client';
import {
  claimsApi,
  clientsApi,
  policiesApi,
  remindersApi,
  useClientActivities,
} from '@/lib/api/domains';
import { useOfflineMutation, PENDING_FLAG } from '@/lib/offline/use-offline-mutation';
import { PendingSyncBadge } from '@/components/offline/offline-indicator';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { CORE_PRODUCT_TYPES, formatPlate, type PolicyDto } from '@ias/shared';

import { ClientFormDialog } from './list';
import { DaysBadge, policyRowTint, policyStatusTone } from '../policies/list';
import { PolicyFormDialog } from '../policies/policy-form';

export function ClientDetailPage() {
  const { id = '' } = useParams();
  const { t, te, money, date } = useI18n();
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: client, isLoading } = clientsApi.useDetail(id);
  // Shares the cache entry with the Policies tab — same key, one request.
  const { data: clientPolicies } = policiesApi.useList({ clientId: id, perPage: 100, sortBy: 'endDate' });
  const [editOpen, setEditOpen] = React.useState(false);
  // Deep link from the client list's "New policy" action.
  const [policyOpen, setPolicyOpen] = React.useState(searchParams.get('new') === 'policy');
  const [policyType, setPolicyType] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!policyOpen && searchParams.get('new')) {
      setSearchParams({}, { replace: true });
    }
  }, [policyOpen, searchParams, setSearchParams]);

  if (isLoading) return <DetailPageSkeleton />;
  if (!client) return <EmptyState title={t('error.notFound')} hint={t('error.notFoundHint')} />;

  const isCompany = client.clientType === 'company';

  return (
    <DetailPage>
      <DetailHeader
        breadcrumbs={[{ label: t('client.title'), to: '/clients' }, { label: client.fullName }]}
        avatar={
          <EntityAvatar name={client.fullName} kind={isCompany ? 'company' : 'person'} size="lg" />
        }
        title={client.fullName}
        status={<EnumBadge enum="clientType" value={client.clientType} />}
        meta={[
          ...(client.taxNumber
            ? [{ key: 'afm', label: <span className="tabular">ΑΦΜ {client.taxNumber}</span> }]
            : []),
          ...(client.phone
            ? [
                {
                  key: 'phone',
                  label: (
                    <a href={`tel:${client.phone}`} className="hover:text-foreground tabular">
                      {client.phone}
                    </a>
                  ),
                },
              ]
            : []),
          ...(client.city ? [{ key: 'city', label: client.city }] : []),
          {
            key: 'agent',
            label: client.assignedAgentName ?? t('common.unassigned'),
          },
        ]}
        actions={
          <>
            {can('policy:write') && (
              <Button onClick={() => setPolicyOpen(true)}>
                <Plus /> {t('action.newPolicy')}
              </Button>
            )}
            {can('client:write') && (
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil /> {t('action.edit')}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => void download(`/clients/${id}/export`, `client-${id}.json`)}
            >
              <Download /> {t('client.exportData')}
            </Button>
          </>
        }
      />

      <DetailMetrics>
        {/*
          The count alone answered «3» to a question the office asks as «3 τι;» — a client
          holding two cars and a health policy read the same as one holding three cars. The
          breakdown is computed from the policies already on the page, so it costs no request.
        */}
        <Metric
          label={t('client.activePolicies')}
          value={client.activePolicyCount ?? 0}
          hint={activeLineMix(clientPolicies?.data, te)}
        />
        <Metric label={t('policy.premium')} value={money(client.totalPremium)} />
        {/*
          «Ανοιχτές ζημιές», not «Ζημιές». The value has always been `openClaimCount`, so a
          client with one settled claim read «Ζημιές 0» beside a Ζημιές tab listing that
          claim — the figure was right and the label was lying about which figure it was.
          Reusing the dashboard's existing key rather than adding a second wording for it.
        */}
        <Metric
          label={t('dashboard.openClaims')}
          value={client.openClaimCount ?? 0}
          tone={(client.openClaimCount ?? 0) > 0 ? 'warning' : undefined}
        />
        <Metric label={t('client.since')} value={date(client.createdAt)} />
      </DetailMetrics>

      <Tabs defaultValue="policies">
        <DetailTabsBar>
          <TabsList variant="underline">
            <TabsTrigger value="policies">{t('policy.title')}</TabsTrigger>
            <TabsTrigger value="claims">{t('claim.title')}</TabsTrigger>
            <TabsTrigger value="info">{t('common.details')}</TabsTrigger>
            <TabsTrigger value="activities">{t('client.activities')}</TabsTrigger>
            <TabsTrigger value="documents">{t('common.documents')}</TabsTrigger>
            <TabsTrigger value="reminders">{t('reminder.title')}</TabsTrigger>
          </TabsList>
        </DetailTabsBar>

        {/*
          The contact rail rides alongside the two tabs where an agent is most likely to
          reach for the phone — looking at what someone holds, or what they have claimed.
          It is supporting information, so it sits in the aside, not above the content.
        */}
        <TabsContent value="policies">
          <Workspace
            aside={
              <ContactRail
                client={client}
                clientPolicies={clientPolicies?.data}
                onPickGap={(type) => {
                  setPolicyType(type);
                  setPolicyOpen(true);
                }}
              />
            }
          >
            <ClientPolicies clientId={id} />
          </Workspace>
        </TabsContent>

        <TabsContent value="claims">
          <Workspace aside={<ContactRail client={client} clientPolicies={clientPolicies?.data} />}>
            <ClientClaims clientId={id} />
          </Workspace>
        </TabsContent>

        <TabsContent value="info">
          <SectionStack>
            <Section title={t('section.identity')}>
              <FieldGrid>
                <Field label={t('client.fullName')}>{client.fullName}</Field>
                <Field label={t('client.taxNumber')} mono>
                  {client.taxNumber ?? '—'}
                </Field>
                <Field label={t('client.idNumber')} mono>
                  {client.idNumber ?? '—'}
                </Field>
                <Field label={t('client.drivingLicense')} mono>
                  {client.drivingLicenseNumber ?? '—'}
                </Field>
                <Field label={t('client.dateOfBirth')} mono>
                  {date(client.dateOfBirth)}
                </Field>
                <Field label={t('client.contactPerson')}>{client.contactPerson ?? '—'}</Field>
              </FieldGrid>
            </Section>

            <Section title={t('section.contact')}>
              <FieldGrid>
                <Field label={t('client.phone')} mono>
                  {client.phone ?? '—'}
                </Field>
                <Field label={t('client.email')}>{client.email ?? '—'}</Field>
                <Field label={t('client.address')}>{client.address ?? '—'}</Field>
                <Field label={t('client.city')}>{client.city ?? '—'}</Field>
                <Field label={t('client.postalCode')} mono>
                  {client.postalCode ?? '—'}
                </Field>
                <Field label={t('client.marketingConsent')}>
                  <Badge tone={client.marketingConsent ? 'good' : 'neutral'} size="sm">
                    {client.marketingConsent ? t('common.yes') : t('common.no')}
                  </Badge>
                </Field>
              </FieldGrid>
            </Section>

            <Section title={t('section.financial')}>
              <FieldGrid>
                <Field label={t('client.iban')} mono>
                  {client.iban ?? '—'}
                </Field>
                <Field label={t('policy.premium')} mono>
                  {money(client.totalPremium)}
                </Field>
                <Field label={t('client.agent')}>
                  {client.assignedAgentName ?? t('common.unassigned')}
                </Field>
              </FieldGrid>
            </Section>

            {client.notes && (
              <Section title={t('section.notes')}>
                <p className="text-[13.5px] leading-relaxed whitespace-pre-line">{client.notes}</p>
              </Section>
            )}
          </SectionStack>
        </TabsContent>

        <TabsContent value="activities">
          <ActivityPanel clientId={id} />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentPanel scope="clients" ownerId={id} typeEnum="clientDocumentType" />
        </TabsContent>

        <TabsContent value="reminders">
          <ClientReminders clientId={id} />
        </TabsContent>
      </Tabs>

      <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} />
      <PolicyFormDialog
        open={policyOpen}
        onOpenChange={(open) => {
          setPolicyOpen(open);
          if (!open) setPolicyType(undefined);
        }}
        clientId={id}
        defaultType={policyType}
      />
    </DetailPage>
  );
}

/**
 * The supporting rail: how to reach this client, how they are labelled, and what they
 * do not yet hold. None of it is the case itself, which is why it sits beside the
 * workspace rather than above it.
 */
function ContactRail({
  client,
  clientPolicies,
  onPickGap,
}: {
  client: {
    phone: string | null;
    email: string | null;
    address: string | null;
    city: string | null;
    postalCode: string | null;
    tags: string[];
    vehicleCount?: number;
  };
  clientPolicies?: PolicyDto[];
  onPickGap?: (type: string) => void;
}) {
  const { t } = useI18n();

  return (
    <SectionStack>
      <Section title={t('section.contact')}>
        <div className="space-y-2.5 text-[13.5px]">
          {client.phone && (
            <a
              href={`tel:${client.phone}`}
              className="hover:text-primary group flex items-center gap-2.5 transition-colors duration-150"
            >
              <Phone className="text-muted-soft size-3.5 shrink-0" />
              <span className="tabular">{client.phone}</span>
            </a>
          )}
          {client.email && (
            <a
              href={`mailto:${client.email}`}
              className="hover:text-primary flex items-center gap-2.5 transition-colors duration-150"
            >
              <Mail className="text-muted-soft size-3.5 shrink-0" />
              <span className="truncate">{client.email}</span>
            </a>
          )}
          {(client.address || client.city) && (
            <p className="flex items-start gap-2.5">
              <MapPin className="text-muted-soft mt-0.5 size-3.5 shrink-0" />
              <span className="leading-snug">
                {client.address}
                {client.address && client.city ? ', ' : ''}
                {client.city} {client.postalCode}
              </span>
            </p>
          )}
          {!client.phone && !client.email && !client.address && !client.city && (
            <p className="text-muted-soft">—</p>
          )}
        </div>
      </Section>

      <VehicleSection clientPolicies={clientPolicies} total={client.vehicleCount} />

      {client.tags.length > 0 && (
        <Section title={t('client.tags')}>
          <div className="flex flex-wrap gap-1.5">
            {client.tags.map((tag) => (
              <Badge key={tag} tone="outline" size="sm">
                {tag}
              </Badge>
            ))}
          </div>
        </Section>
      )}

      {onPickGap && <CrossSellChips clientPolicies={clientPolicies} onPick={onPickGap} />}
    </SectionStack>
  );
}

/**
 * Every vehicle the client's file holds, each marked with whether it is still on cover.
 *
 * The office asked for the whole fleet, not just what is live: a call about a car sold three
 * years ago still has to find its owner, and the tag is what keeps that from reading as an
 * active policy. Folded per plate rather than per policy — the archive carries a decade of
 * annual renewals for one car, and listing that car ten times answers nothing.
 *
 * Derived from the policies the tab already loaded, so the rail costs no request of its own.
 * `total` is the server's count over the full archive and wins the heading when the two
 * disagree, which happens only for a client past the 100-policy page.
 */
function VehicleSection({
  clientPolicies,
  total,
}: {
  clientPolicies?: PolicyDto[];
  total?: number;
}) {
  const { t } = useI18n();

  const vehicles = React.useMemo(() => {
    const live = new Map<string, boolean>();
    for (const policy of clientPolicies ?? []) {
      const onCover = policy.status === 'active' || policy.status === 'renewal_due';
      for (const plate of policy.plates ?? []) {
        // A plate counts as insured if ANY of its policies is live, so an old expired one
        // beside this year's does not mark the car as lapsed.
        live.set(plate, (live.get(plate) ?? false) || onCover);
      }
    }
    return [...live.entries()]
      // Insured first — the live fleet is what the agent is usually looking at.
      .sort(([plateA, activeA], [plateB, activeB]) =>
        activeA === activeB ? plateA.localeCompare(plateB) : activeA ? -1 : 1,
      )
      .map(([plate, active]) => ({ plate, active }));
  }, [clientPolicies]);

  if (!vehicles.length) return null;

  const activeCount = vehicles.filter((vehicle) => vehicle.active).length;

  return (
    <Section
      title={`${t('client.vehicles')} · ${total ?? vehicles.length}`}
      description={
        // Silent when the whole fleet is on cover: the per-row «Ασφαλισμένο» tags already say
        // so, and «5 · 5 με ενεργό συμβόλαιο» is the same number twice.
        activeCount === vehicles.length
          ? undefined
          : activeCount
            ? t('client.vehiclesHint', { active: activeCount })
            : t('client.vehiclesNoneActive')
      }
    >
      <div className="space-y-1.5">
        {vehicles.map(({ plate, active }) => (
          <Link
            key={plate}
            to={`/policies?plateNumber=${plate}`}
            className="hover:bg-accent -mx-2 flex items-center gap-2 rounded-md px-2 py-1 transition-colors duration-150"
          >
            <Car className="text-muted-soft size-3.5 shrink-0" />
            <span className="tabular text-[13.5px] font-medium">{formatPlate(plate)}</span>
            <Badge tone={active ? 'good' : 'outline'} size="sm" className="ml-auto">
              {active ? t('vehicle.insured') : t('vehicle.notInsured')}
            </Badge>
          </Link>
        ))}
      </div>
    </Section>
  );
}

/**
 * «3 ενεργά συμβόλαια» of what. Returns the mix as «2 αυτοκίνητο · 1 υγείας», or the bare
 * line name when there is only one — a client with three car policies does not need the
 * number said twice.
 */
function activeLineMix(
  policies: PolicyDto[] | undefined,
  te: (group: string, value: string) => string,
): string | undefined {
  const counts = new Map<string, number>();
  for (const policy of policies ?? []) {
    if (policy.status !== 'active' && policy.status !== 'renewal_due') continue;
    counts.set(policy.insuranceType, (counts.get(policy.insuranceType) ?? 0) + 1);
  }
  if (!counts.size) return undefined;

  return [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .map(([type, count]) => (counts.size === 1 ? te('insuranceType', type) : `${count} ${te('insuranceType', type)}`))
    .join(' · ');
}

/**
 * Cross-sell gap chips (review pass 2): the core lines this client does NOT hold.
 * Pure client-side comparison over data already on the page — the gap-analysis
 * feature every benchmarked AMS ships, at zero extra queries.
 */
function CrossSellChips({
  clientPolicies,
  onPick,
}: {
  clientPolicies: { insuranceType: string; status: string }[] | undefined;
  onPick: (type: string) => void;
}) {
  const { t, te } = useI18n();
  const { can } = useAuth();

  if (!can('policy:write') || !clientPolicies) return null;

  const owned = new Set(
    clientPolicies
      .filter((policy) => ['active', 'renewal_due', 'paid', 'pending'].includes(policy.status))
      .map((policy) => policy.insuranceType),
  );
  const gaps = CORE_PRODUCT_TYPES.filter((type) => !owned.has(type));
  if (!gaps.length) return null;

  return (
    <Section title={t('client.opportunities')} description={t('client.opportunitiesHint')}>
      <div className="flex flex-wrap gap-1.5">
        {gaps.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => onPick(type)}
            className="border-border-strong text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-primary/[0.06] inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-1 text-xs font-medium transition-colors duration-150"
          >
            + {te('insuranceType', type)}
          </button>
        ))}
      </div>
    </Section>
  );
}

function ClientPolicies({ clientId }: { clientId: string }) {
  const { t, money, date } = useI18n();
  /*
   * Λήξη descending: το τρέχον συμβόλαιο πάνω-πάνω, το ιστορικό από κάτω.
   *
   * Ascending έβαζε πρώτο ό,τι έληξε πριν από χρόνια, οπότε σε πελάτη με δέκα ανανεώσεις
   * του ίδιου αυτοκινήτου το μόνο συμβόλαιο που ισχύει σήμερα ήταν στο τέλος της λίστας.
   * Η ερώτηση που κάνει το γραφείο ανοίγοντας την καρτέλα είναι «τι τρέχει τώρα», όχι
   * «τι έτρεχε το 2024».
   */
  const { data, isLoading } = policiesApi.useList({
    clientId,
    perPage: 100,
    sortBy: 'endDate',
    sortOrder: 'desc',
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data?.data.length)
    return (
      <EmptyState title={t('common.empty')} hint={t('policy.title')} compact />
    );

  return (
    <Panel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('policy.number')}</TableHead>
            <TableHead>{t('policy.company')}</TableHead>
            <TableHead>{t('policy.endDate')}</TableHead>
            <TableHead className="text-right">{t('policy.premium')}</TableHead>
            <TableHead>{t('policy.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.data.map((policy) => (
            <TableRow key={policy.id} className={policyRowTint(policy)}>
              <TableCell className="py-2.5">
                {/*
                  One line, not two. A client's policy tab is often ten rows of «auto» differing
                  only by number, and which car each row is for was not on the row at all; it used
                  to sit underneath in its own muted line, hyphenated. `PolicyRef` puts it in front
                  of the number instead — the order the office says it — and spells it the one way
                  the rest of the app now spells it. A health policy has no plate and the number
                  simply takes the line.
                */}
                <Link to={`/policies/${policy.id}`} className="text-primary font-medium hover:underline">
                  <PolicyRef policyNumber={policy.policyNumber} plates={policy.plates} muted />
                </Link>
              </TableCell>
              <TableCell className="text-muted-foreground py-2.5">{policy.insuranceCompanyName}</TableCell>
              <TableCell className="py-2.5">
                <span className="flex items-center gap-2">
                  <span className="tabular">{date(policy.endDate)}</span>
                  <DaysBadge days={policy.daysUntilExpiry} />
                </span>
              </TableCell>
              <TableCell className="py-2.5 text-right tabular">{money(policy.premiumAmount)}</TableCell>
              <TableCell className="py-2.5">
                <EnumBadge enum="policyStatus" value={policy.status} tone={policyStatusTone(policy)} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

function ClientClaims({ clientId }: { clientId: string }) {
  const { t, money, date } = useI18n();
  const { data, isLoading } = claimsApi.useList({ clientId, perPage: 100 });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data?.data.length)
    return (
      <EmptyState title={t('common.empty')} hint={t('claim.title')} compact />
    );

  return (
    <Panel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('claim.number')}</TableHead>
            <TableHead>{t('claim.incidentDate')}</TableHead>
            <TableHead className="text-right">{t('claim.amount')}</TableHead>
            <TableHead>{t('policy.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.data.map((claim) => (
            <TableRow key={claim.id}>
              <TableCell className="py-2.5">
                <Link to={`/claims/${claim.id}`} className="text-primary font-medium hover:underline">
                  {claim.claimNumber ?? claim.id.slice(0, 8)}
                </Link>
              </TableCell>
              <TableCell className="py-2.5 tabular">{date(claim.incidentDate)}</TableCell>
              <TableCell className="py-2.5 text-right tabular">{money(claim.claimAmount)}</TableCell>
              <TableCell className="py-2.5">
                <EnumBadge enum="claimStatus" value={claim.status} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

function ClientReminders({ clientId }: { clientId: string }) {
  const { t, date } = useI18n();
  const { data, isLoading } = remindersApi.useList({ clientId, perPage: 50 });
  const rows = (data?.data ?? []) as { id: string; title: string; reminderDate: string; status: string }[];

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (!rows.length)
    return (
      <EmptyState title={t('common.empty')} hint={t('reminder.title')} compact />
    );

  return (
    <Panel className="divide-border divide-y">
        {rows.map((reminder) => (
          <div key={reminder.id} className="flex items-center justify-between gap-3 px-5 py-3">
            <span className="text-sm">{reminder.title}</span>
            <span className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs tabular">{date(reminder.reminderDate)}</span>
              <EnumBadge enum="reminderStatus" value={reminder.status} size="sm" />
            </span>
          </div>
        ))}
      </Panel>
  );
}

/** Communication log — a CRM staple the spec omits entirely. */
function ActivityPanel({ clientId }: { clientId: string }) {
  const { t, dateTime } = useI18n();
  const { can } = useAuth();
  const { data, isLoading } = useClientActivities(clientId);
  const [open, setOpen] = React.useState(false);

  return (
    <div className="space-y-3">
      {can('client:write') && (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <MessageSquarePlus /> {t('client.newActivity')}
        </Button>
      )}

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !data?.length ? (
        <EmptyState title={t('common.empty')} hint={t('client.activities')} compact />
      ) : (
        <Panel className="divide-border divide-y">
            {data.map((activity) => (
              /*
                The type used to sit in a 28px round swatch beside the subject. It could not:
                `EnumBadge variant="dot"` renders a dot AND its label, so «Τηλεφώνημα» was
                being centred inside a circle a third of its width and overflowed in both
                directions, printing itself on top of the subject next to it. Two strings drawn
                over each other, on every row.

                So the type moves to where a label fits — inline on the subject line, as the
                badge it already is. Nothing is lost and the row gains the thing the swatch was
                really for: you can tell a call from an email without reading the free-text
                subject, which is written by whoever logged it and cannot be relied on.
              */
              <div key={activity.id} className="px-5 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium">
                    <EnumBadge enum="activityType" value={activity.activityType} size="sm" />
                    <span className="min-w-0">{activity.subject}</span>
                    {PENDING_FLAG in activity && <PendingSyncBadge />}
                  </p>
                  <span className="text-muted-foreground shrink-0 text-xs tabular">
                    {dateTime(activity.occurredAt)}
                  </span>
                </div>
                {activity.body && <p className="text-muted-foreground mt-1 text-[13px]">{activity.body}</p>}
                <p className="text-muted-foreground/70 mt-1 text-[11px]">
                  <EnumBadge enum="direction" value={activity.direction} size="sm" variant="dot" />
                  {activity.createdByName ? ` · ${activity.createdByName}` : ''}
                </p>
              </div>
            ))}
          </Panel>
      )}

      <ActivityDialog clientId={clientId} open={open} onOpenChange={setOpen} />
    </div>
  );
}

function ActivityDialog({
  clientId,
  open,
  onOpenChange,
}: {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const typeOptions = useEnumOptions('activityType');
  const directionOptions = useEnumOptions('direction');
  // A visit note is the archetypal offline write — captured at the client's premises,
  // where the connection usually is not.
  const create = useOfflineMutation<Record<string, unknown>>({
    operation: 'activity.create',
    targetId: () => clientId,
    summary: (input) => String(input.subject ?? ''),
    optimistic: (input, entryId) => ({
      queryKey: ['clients', 'detail', clientId, 'activities'],
      record: {
        id: entryId,
        clientId,
        activityType: input.activityType,
        direction: input.direction,
        subject: input.subject,
        body: input.body ?? null,
        occurredAt: new Date().toISOString(),
        policyId: null,
        claimId: null,
        createdBy: null,
        createdByName: null,
        createdAt: new Date().toISOString(),
        [PENDING_FLAG]: true,
      },
    }),
    onSuccessMessage: 'common.saved',
  });

  const [activityType, setActivityType] = React.useState('call');
  const [direction, setDirection] = React.useState('outbound');
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');

  const submit = async () => {
    await create.mutate({ activityType, direction, subject, body: body || null });
    setSubject('');
    setBody('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('client.newActivity')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('enum.activityType.note')}</Label>
              <Select value={activityType} onValueChange={setActivityType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {typeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('enum.direction.outbound')}</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {directionOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="activity-subject">{t('reminder.one')}</Label>
            <Input id="activity-subject" value={subject} onChange={(e) => setSubject(e.target.value)} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="activity-body">{t('common.notes')}</Label>
            <Textarea id="activity-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('action.cancel')}</Button>
          <Button disabled={subject.trim().length < 1} loading={create.isPending} onClick={() => void submit()}>
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
